import { Theme } from '@mui/material/styles';

import {
  ExtraTypographyTypeVariants,
  GetVariantStylesType,
  StyleMapVariant,
  TypographyStyleObject
} from '../../types/TypographyTypes';
import { APTheme } from '../../TenantProvider/tenants';

interface StylesMap {
  h1: StyleMapVariant;
  h2: StyleMapVariant;
  h3: StyleMapVariant;
  body1: StyleMapVariant;
  body2: StyleMapVariant;
  subtitle1: StyleMapVariant;
  subtitle2: StyleMapVariant;
}

const getTypographyStyles = (
  variant: string | keyof StylesMap | undefined,
  type: keyof ExtraTypographyTypeVariants,
  theme: APTheme
): TypographyStyleObject => {
  const {
    displayHugeBold,
    displayLargeBold,
    displayMediumNorm,
    h1,
    h2,
    h3,
    body1,
    body2,
    subtitle1,
    subtitle2,
    buttonLarge,
    buttonMedium,
    buttonSmall
  } = theme.customTypography;

  const getStyles = (baseStyle: TypographyStyleObject, typeStyles: GetVariantStylesType) => {
    return {
      ...baseStyle,
      ...typeStyles,
    };
  };

  const getVariantStyles = (variantStyle: keyof StylesMap = 'body1', variantType: keyof ExtraTypographyTypeVariants) => {
    const stylesMap: StylesMap = {
      h1: {
        base: h1.normal,
        types: {
          bold: h1.bold,
          medium: h1.medium,
          'display-huge-bold': displayHugeBold,
          'display-large-bold': displayLargeBold,
          'display-medium-norm': displayMediumNorm,
        },
      },
      h2: {
        base: h2.normal,
        types: {
          bold: h2.bold,
          medium: h2.medium,
        },
      },
      h3: {
        base: h3.normal,
        types: {
          bold: h3.bold,
          medium: h3.medium,
        },
      },
      body1: {
        base: body1.normal,
        types: {
          bold: body1.bold,
          medium: body1.medium,
          link: body1.link,
          'alt-link': body1.altLink,
          'button-large': buttonLarge,
        },
      },
      body2: {
        base: body2.normal,
        types: {
          bold: body2.bold,
          medium: body2.medium,
          link: body2.link,
          'alt-link': body2.altLink,
          'button-medium': buttonMedium,
          'button-small': buttonSmall,
        },
      },
      subtitle1: {
        base: subtitle1.normal,
        types: {
          bold: subtitle1.bold,
          medium: body2.medium,
          link: subtitle1.link,
          'alt-link': subtitle1.altLink,
        },
      },
      subtitle2: {
        base: subtitle2.normal,
        types: {
          bold: subtitle2.bold,
          link: subtitle2.link,
          'alt-link': subtitle2.altLink,
        },
      },
    };

    const variantStyles: StyleMapVariant = stylesMap[variantStyle];

    if (!variantStyles) {
      return body1.normal;
    }

    const typeStyle: GetVariantStylesType = variantStyles.types[variantType];

    return getStyles(variantStyles.base as TypographyStyleObject, typeStyle) ?? body1.normal;
  };

  return getVariantStyles(variant as keyof StylesMap, type);
};

export const getH1DisplayHugeBoldStyles = (
  theme: APTheme
): TypographyStyleObject =>
  getTypographyStyles('h1', 'display-huge-bold', theme);

export const getH1DisplayLargeBoldStyles = (
  theme: APTheme
): TypographyStyleObject =>
  getTypographyStyles('h1', 'display-large-bold', theme);

export const getH1DisplayMediumNormStyles = (
  theme: APTheme
): TypographyStyleObject =>
  getTypographyStyles('h1', 'display-medium-norm', theme);

export const getH1BoldStyles = (theme: APTheme): TypographyStyleObject =>
  getTypographyStyles('h1', 'bold', theme);

export const getH1NormalStyles = (theme: APTheme): TypographyStyleObject =>
  getTypographyStyles('h1', 'normal', theme);

export const getH1MediumStyles = (theme: APTheme): TypographyStyleObject =>
  getTypographyStyles('h1', 'medium', theme);

export const getH2BoldStyles = (theme: APTheme): TypographyStyleObject =>
  getTypographyStyles('h2', 'bold', theme);

export const getH2NormalStyles = (theme: APTheme): TypographyStyleObject =>
  getTypographyStyles('h2', 'normal', theme);

export const getH2MediumStyles = (theme: APTheme): TypographyStyleObject =>
  getTypographyStyles('h2', 'medium', theme);

export const getH3BoldStyles = (theme: APTheme): TypographyStyleObject =>
  getTypographyStyles('h3', 'bold', theme);

export const getH3NormalStyles = (theme: APTheme): TypographyStyleObject =>
  getTypographyStyles('h3', 'normal', theme);

export const getH3MediumStyles = (theme: APTheme): TypographyStyleObject =>
  getTypographyStyles('h3', 'medium', theme);

export const getBody1BoldStyles = (theme: APTheme): TypographyStyleObject =>
  getTypographyStyles('body1', 'bold', theme);

export const getBody1NormalStyles = (theme: APTheme): TypographyStyleObject =>
  getTypographyStyles('body1', 'normal', theme);

export const getBody1MediumStyles = (theme: APTheme): TypographyStyleObject =>
  getTypographyStyles('body1', 'medium', theme);

export const getBody2BoldStyles = (theme: APTheme): TypographyStyleObject =>
  getTypographyStyles('body2', 'bold', theme);

export const getBody2NormalStyles = (theme: APTheme): TypographyStyleObject =>
  getTypographyStyles('body2', 'normal', theme);

export const getBody2MediumStyles = (theme: APTheme): TypographyStyleObject =>
  getTypographyStyles('body2', 'medium', theme);

export const getBodySmallBoldStyles = (theme: APTheme): TypographyStyleObject =>
  getTypographyStyles('subtitle1', 'bold', theme);

export const getBodySmallMediumStyles = (theme: APTheme): TypographyStyleObject =>
  getTypographyStyles('subtitle1', 'medium', theme);

export const getBodySmallNormalStyles = (theme: APTheme): TypographyStyleObject =>
  getTypographyStyles('subtitle1', 'normal', theme);

export const getBodyExtraSmallBoldStyles = (
  theme: APTheme
): TypographyStyleObject => getTypographyStyles('subtitle2', 'bold', theme);

export const getBodyExtraSmallNormalStyles = (
  theme: APTheme
): TypographyStyleObject => getTypographyStyles('subtitle2', 'normal', theme);

export const getBody1ButtonLargeStyles = (theme: APTheme): TypographyStyleObject =>
  getTypographyStyles('body1', 'button-large', theme);

export const getBody2ButtonMediumStyles = (theme: APTheme): TypographyStyleObject =>
  getTypographyStyles('body2', 'button-medium', theme);

export default getTypographyStyles;
